/**
 * Web push helpers for the launcher. One browser subscription covers every app;
 * it is saved once per device token (one per circle) with devices.savePushSubscription.
 */
import { registerServiceWorker } from './sw';

/** VAPID public key (base64url) → the bytes pushManager.subscribe wants. */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** False on iOS Safari outside an installed home-screen app, and on old browsers. */
export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported';
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false;
  const x = new Uint8Array(a);
  return x.length === b.length && x.every((v, i) => v === b[i]);
}

/** The existing subscription, if any (does not prompt). */
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await registerServiceWorker();
  return reg ? reg.pushManager.getSubscription() : null;
}

/**
 * Subscribes (or reuses the existing subscription) and returns its JSON form.
 * Must be called after permission is granted, or from a user gesture so the
 * browser can prompt. Re-subscribes if the server's VAPID key changed.
 */
export async function subscribePush(publicKey: string): Promise<PushSubscriptionJSON> {
  const reg = await registerServiceWorker();
  if (!reg) throw new Error('Service worker unavailable');
  const key = urlBase64ToUint8Array(publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  return sub.toJSON();
}
